import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { apiClient } from '../lib/api-client'
import { useReturnDestination, withReturnPath } from '../lib/returnPath'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Logo } from '../components/shell/Logo'

/** Email confirmation uses the shared platform; parental verification stays separate. */
export function EmailVerificationPage({ code = false }: { code?: boolean }) {
  const [params] = useSearchParams()
  const token = params.get('token') ?? ''
  const returnTo = useReturnDestination()
  return code ? <CodeConfirmation returnTo={returnTo} /> : <EmailConfirmation key={token} token={token} returnTo={returnTo} />
}

function useConfirmation() {
  const [busy, setBusy] = useState(false)
  const [verified, setVerified] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const mounted = useRef(true)
  const inFlight = useRef(false)
  useEffect(() => { mounted.current = true; return () => { mounted.current = false } }, [])

  async function confirm(request: () => Promise<void>) {
    if (verified || inFlight.current) return
    inFlight.current = true
    setBusy(true)
    setError(null)
    try {
      await request()
      if (mounted.current) setVerified(true)
    } catch (err) {
      if (mounted.current) setError(err instanceof Error ? err.message : 'This email could not be confirmed. Please try again.')
    } finally {
      inFlight.current = false
      if (mounted.current) setBusy(false)
    }
  }

  return { busy, verified, error, confirm }
}

function ConfirmationCard({ verified, returnTo, children }: { verified: boolean; returnTo?: string; children: ReactNode }) {
  return <div className="flex min-h-screen items-center justify-center bg-paper-page px-5 py-8"><div className="w-full max-w-[440px] rounded-card border border-line bg-paper-surface p-6 shadow-card">
    <Logo />
    <h1 className="mt-5 font-serif text-[28px]">{verified ? 'Email confirmed' : 'Confirm your email'}</h1>
    {verified ? <p role="status" className="mt-4 text-sm text-moss">Email verified. Sign in to continue.</p> : children}
    <p className="mt-4 text-xs text-ink-muted">Parent verification, if required, is a separate step.</p>
    <Link className="mt-5 inline-block text-sm text-terra-deep" to={withReturnPath('/login', returnTo)}>Back to sign in</Link>
  </div></div>
}

function EmailConfirmation({ token, returnTo }: { token: string; returnTo?: string }) {
  const { busy, verified, error, confirm } = useConfirmation()
  return <ConfirmationCard verified={verified} returnTo={returnTo}>
    {!token ? <p role="alert" className="mt-4 text-sm text-honey-fg">This link is missing its token. Open the full verification link from your email.</p> : <div className="mt-5 grid gap-4">
      <p className="text-sm text-ink-muted">Confirm the email address used for your Circle of Trust account.</p>
      {error && <p role="alert" className="text-sm text-honey-fg">{error}</p>}
      <Button onClick={() => { void confirm(() => apiClient.verifyEmail(token)) }} disabled={busy}>{busy ? 'Confirming…' : 'Confirm email'}</Button>
    </div>}
    <Link className="mt-4 inline-block text-sm text-terra-deep" to={withReturnPath('/confirm-email', returnTo)}>Use a confirmation code</Link>
  </ConfirmationCard>
}

function CodeConfirmation({ returnTo }: { returnTo?: string }) {
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const { busy, verified, error, confirm } = useConfirmation()
  const valid = email.trim().length > 0 && /^[0-9]{6}$/.test(code)
  return <ConfirmationCard verified={verified} returnTo={returnTo}>
    <form className="mt-5 grid gap-4" onSubmit={(event) => { event.preventDefault(); if (valid) void confirm(() => apiClient.verifyEmailCode(email.trim(), code)) }}>
      <p className="text-sm text-ink-muted">Enter the email address you registered with and the six-digit code from your email.</p>
      <Input label="Email" type="email" autoComplete="email" required disabled={busy} value={email} onChange={(event) => setEmail(event.target.value)} />
      <Input label="Confirmation code" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6} required disabled={busy} value={code} onChange={(event) => setCode(event.target.value)} />
      {error && <p role="alert" className="text-sm text-honey-fg">{error}</p>}
      <Button type="submit" disabled={!valid || busy}>{busy ? 'Confirming…' : 'Confirm email'}</Button>
    </form>
  </ConfirmationCard>
}
